import { useState, useEffect, useMemo, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import BigNumber from 'bignumber.js'
import {
  useApprovalModalStateDispatch,
  useModalOpened,
  useModalState,
  useModalToggle,
} from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import {
  formatAmount,
  getTokenPermit,
  showSkeletonStateList,
} from '../../helpers'
import IconSwapArrow from '~/assets/images/swap-arrow.svg'
// import IconLonToken from '~/assets/images/lon-token.svg'
// import IconArrow from '~/assets/images/arrow-right-purple.svg'
import {
  StyledOrderPreview,
  BackButton,
  Title,
  Content,
  Row,
  StyledTipValue,
  // StyledRewards,
  // StyledRewardsValueArea,
  StyledDivider,
  StyledDividerLite,
  CountdownTitle,
  CountdownNumber,
  QuoteExpiredTitle,
} from './styled'
import TradeForm from '~/components/TradeForm'
import ExchangeButton from '../ExchangeButton'
import { TRADE_STATUS } from '~/components/TradeDetail/types'
import { AddressZero } from '@ethersproject/constants'
import { SpotSwapUserTradeStatus } from '~/state/dex/type'
import {
  useTokenlonConfig,
  useSpotSwapPair,
  useSpotSwapAmount,
  useSpotSwapPriceImpact,
  PriceImpactLevel,
  useTokenlonV6NetworkConfig,
  useSpotSwapUserConfig,
} from '~/state/dex/hooks'
import {
  updateSpotSwapAmount,
  updateSpotSwapPairAmount,
  updateSpotSwapUserTradeStatus,
} from '~/state/dex/actions'
import Countdown from 'react-countdown'
import OrderStatus from './components/OrderStatus'
import { useGA, useIsHoT, useIsContractAddress } from '~/hooks'
import {
  useIsSendByRelayer,
  useSettlementFeeValidator,
  useGasPrice,
  useGasLimitScale,
} from '~/components/Exchange/hooks'
import { useV6SDK } from '~/hooks/useV6SDK'
import GasFeeRow from '../GasFeeRow'
import { FixedPointNumber } from '~/utils/fixedPoint'
import {
  SubmitDTO,
  Order,
  genericSwapAddress,
  OrderStatus as OrderStatusType,
  CrossSwapOrderStatus as CrossOrderStatusType,
  SwapData,
  QuoteDTO,
  CrossSwapOrderStatus,
} from '@tokenlon/v6-sdk'
import { DEFAULT_GAS_LIMIT } from '~/constants/unit'
import { PermitSource } from '~/components/Exchange/types'
import { useWeb3React } from '@web3-react/core'
import { formatEther, formatUnits, Address, Hex, parseUnits } from 'viem'
import useSWR from 'swr'
import { message } from '~/components/Message'
import { handleTxError } from '~/utils/handleTxError'
import { useAppDispatch } from '~/state/hooks'
import { useUpdaterChainId } from '~/hooks/web3'
import { useTokenPermit } from '~/hooks/useTokenAllowance'
import { isMainnetOrSepolia } from '~/utils/chain'
import { compareString, delay } from '~/utils/common'
import { ApprovalProgress } from '~/components/ApprovalModal/ApproveStatusLabel'
import OrderSuccessful from '../OrderSuccessful'
import { RateState } from '../../types'
import MinReceived from '../MinReceived'
import TokensChangeArea from './TokensChangeArea'
import ReceivedRow from './ReceivedRow'
import SlippageRow from './SlippageRow'
import TradeFee from './components/TradeFee'
import CrossEstimatedTime from './components/CrossEstimatedTime'
import RecipientPreview from './components/RecipientPreview'
import GasFeeRiskCalculator from '../../components/GasFeeRiskCalculator'
import SettlementFeeRiskCalculator from '../../components/SettlementFeeRiskCalculator'
import PriceImpactConfirm from './PriceImpactConfirm'
import OrderRefreshing from './OrderRefreshing'
import { TradeDirection } from '~/components/Exchange/types'
import InteractiveMascot from '../../components/InteractiveMascot'
import { useNativeTokenBalance } from '~/state/wallet/hooks'
import { toBigNumber } from '~/utils/numbers'
import { getIsOrderContainsBebop } from '../SmartRoutingVisualizer/utils'
import { applyGasUsedBuffer } from '~/utils/gasSpeedup'
import {
  getIsFailed,
  getIsSuccess,
} from '~/components/TradeDetail/components/CrossSwapOrderStatus/help'
import ConfirmRecipientModal from '../Recipient/ConfirmRecipientModal'
import { trackCrossChainTx } from '~/components/Exchange/helpers'

interface OrderPreviewProps {
  order: Order
  // use bp as unit
  slippage: number
  isUseAllowanceTarget: boolean
  setLockOrder: (order: Order | null) => void
  setIsUserEdited: (isUserEdited: boolean) => void
}

const SCAM_SNIFFER_CANCEL_ERR_MSG = 'return null, maybe cancel by ScamSniffer.'
const POPUP_ERR_MSG = 'WebApp window failed to open'

const OrderPreview: React.FC<OrderPreviewProps> = ({
  order,
  slippage,
  isUseAllowanceTarget,
  setLockOrder,
  setIsUserEdited,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { trackClick, trackView } = useGA()
  const sdk = useV6SDK()
  const isHoT = useIsHoT()

  const { account: address, chainId: currentChainId } = useWeb3React()
  const account = address as Address
  const expectChainId = useUpdaterChainId()
  const isMainnetOrSepoliaFlag = isMainnetOrSepolia(expectChainId)

  const [inputToken, outputToken] = useSpotSwapPair()
  const { inputAmount, outputAmount } = useSpotSwapAmount()
  const { mmOrderExpirationSeconds } = useTokenlonConfig()
  const [gasFeeTokenAmount, setGasFeeTokenAmount] = useState(0n)
  const [amountOut, setAmountOut] = useState<bigint | undefined>()
  const [minAmountOut, setMinAmountOut] = useState<bigint | undefined>()
  const [fee, setFee] = useState<bigint>(0n)
  const [feeFactor, setFeeFactor] = useState(isMainnetOrSepoliaFlag ? 30 : 15)
  const [txHash, setTxHash] = useState<string | undefined>()
  const [isTrackingCrossTxByEvent, setIsTrackingCrossTxByEvent] = useState(true)

  const isOrderContainsBebop = useMemo(
    () => getIsOrderContainsBebop(order?.routes),
    [order?.routes],
  )

  const { isCrossSwap, recipient } = useSpotSwapUserConfig()
  const isContractWallet = useIsContractAddress(account, expectChainId)
  const [
    isOpenConfirmRecipientModal,
    setIsOpenConfirmRecipientModal,
  ] = useState(false)

  // 预估留给用户签名交互的时间
  const userSignatureTime = 20
  const initialExpiredTime =
    isOrderContainsBebop && !!order?.swapData?.expiry
      ? (order?.swapData?.expiry - userSignatureTime) * 1000
      : Date.now() + mmOrderExpirationSeconds * 1000
  const [expiredTime, setExpiredTime] = useState(initialExpiredTime)

  const [orderRefreshing, setOrderRefreshing] = useState(false)
  const [isOrderExpired, setIsOrderExpired] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [orderStatus, setOrderStatus] = useState<
    OrderStatusType | CrossOrderStatusType
  >(OrderStatusType.Unbroadcast)
  const [uniqueId, setUniqueId] = useState<string | undefined>()
  const [outputTokenAmount, setOutputTokenAmount] = useState(outputAmount)
  // @notice: walletconnect 断开连接后会重新渲染，order 变会 undefined
  const inputTokenAmount = order?.amountIn ?? inputAmount

  const nativeTokenBalance = useNativeTokenBalance()

  const [showSuccessful, setShowSuccessful] = useState(false)
  const [rateState, setRateState] = useState(RateState.LOADING)
  const [crossEstimatedSec, setCrossEstimatedSec] = useState(300)
  // 价格影响很高时，需要用户勾选确认
  const priceImpactInfo = useSpotSwapPriceImpact()
  const [highPriceImpactConfirm, setHighPriceImpactConfirm] = useState(false)

  const isValidOrder = useRef(true)

  const isSettlementFeeValid = useSettlementFeeValidator(minAmountOut)

  const isPreviewOpen = useModalOpened(ApplicationModal.SWAP_ORDER_PREVIEW)
  const togglePreviewModal = useModalToggle(ApplicationModal.SWAP_ORDER_PREVIEW)
  const isApprovalModalOpen = useModalOpened(ApplicationModal.APPROVAL_STATUS)
  const { approvalState } = useModalState(ApplicationModal.APPROVAL_STATUS)
  const toggleSubmitParamsModal = useModalToggle(
    ApplicationModal.APPROVAL_STATUS,
    {
      stack: true,
      modalState: {
        title: t('submitParamsModal.title'),
        description1: t('submitParamsModal.description_1'),
        description2: t('submitParamsModal.description_2'),
        showLogo: false,
        token: inputToken,
        approvalState,
        progress: ApprovalProgress.APPROVING,
      },
    },
  )
  const togglePermitSingleModal = useModalToggle(
    ApplicationModal.APPROVAL_STATUS,
    {
      stack: true,
      modalState: {
        title: t('permit2ContractModal.title'),
        description1: t('permit2ContractModal.description_1'),
        description2: t('permit2ContractModal.description_2'),
        showLogo: true,
        token: inputToken,
        approvalState,
        progress: ApprovalProgress.APPROVING,
      },
    },
  )
  const updateApprovalState = useApprovalModalStateDispatch()

  const rate = order?.rate ? new BigNumber(order?.rate).toFixed() : '0'
  const calcScale = +rate < 1e-6 ? 12 : 6

  const {
    defaultGasLimit: networkDefaultGasLimit,
  } = useTokenlonV6NetworkConfig()
  const defaultGasLimit = useMemo(() => {
    return BigInt(networkDefaultGasLimit || DEFAULT_GAS_LIMIT)
  }, [networkDefaultGasLimit])

  const { gasPriceWithScale, maxPriorityFeePerGas } = useGasPrice()
  const gasLimitScale = useGasLimitScale()

  // @notice 当前都是 AMM
  const isAMMOrder = true

  // 监听送上链的 order 的状态
  const { data: orderState } = useSWR(
    uniqueId ? ['getOrderState', uniqueId] : null,
    async () =>
      await sdk.swap.status({
        uniqueId,
        toChainId: isCrossSwap ? outputToken.chainId : null,
      }),
    { refreshInterval: 1000 },
  )

  const isTakerETH = inputToken?.address === AddressZero
  const isSendByRelayer = useIsSendByRelayer()

  const { tokenPermitAmount, tokenPermitExpiration } = useTokenPermit(
    inputToken?.address as Address,
    genericSwapAddress[expectChainId],
  )
  const isNotEnoughTokenPermit = new FixedPointNumber(
    inputAmount,
    inputToken.decimals,
  ).gt(tokenPermitAmount?.toString() ?? '0')

  const isPermit2AllowanceExpired =
    Math.floor(Date.now() / 1000) > Number(tokenPermitExpiration)

  const permitData = useMemo(() => {
    const permitSource = isUseAllowanceTarget
      ? PermitSource.TokenlonAllowanceTarget
      : PermitSource.Permit2AllowanceTransfer
    return sdk.swap.getTokenPermitData(
      inputToken?.address as Address,
      permitSource,
    )
  }, [isUseAllowanceTarget, inputToken?.address])

  const componentLoading = useMemo(
    () => showSkeletonStateList.includes(rateState) || orderRefreshing,
    [rateState, orderRefreshing],
  )

  const isAcrossSwap = useMemo(() => {
    return isCrossSwap && order?.routes?.[0]?.[0]?.[0]?.protocol === 'across'
  }, [order?.uniqueId])

  const isUnbroadcast = orderStatus === OrderStatusType.Unbroadcast
  const priceImpactIsDanger = priceImpactInfo?.level === PriceImpactLevel.DANGER

  const recipientModalCloseHandler = useRef<(confirmed: boolean) => void>()
  const showConfirmModal = (): Promise<boolean> => {
    return new Promise((resolve) => {
      setIsOpenConfirmRecipientModal(true)
      const handleClose = (confirmed: boolean) => {
        setIsOpenConfirmRecipientModal(false)
        resolve(confirmed)
      }
      recipientModalCloseHandler.current = handleClose
    })
  }

  const handleClick = async () => {
    trackClick('webtl_instant_preview_confirm', {
      taker_token_symbol: outputToken.symbol,
      maker_token_symbol: inputToken.symbol,
      taker_token_amount: outputTokenAmount,
      maker_token_amount: inputTokenAmount,
      click_timestamp: new Date().getTime(),
      is_cross_swap: isCrossSwap,
    })
    if (isOrderExpired) {
      setOrderRefreshing(true)
      handleRefreshOrder()
      return
    }
    if (isCrossSwap && isContractWallet) {
      const confirmed = await showConfirmModal()
      if (!confirmed) return
    }
    handleExchange()
  }

  const handleRefreshOrder = async () => {
    dispatch(updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.INQUIRING))
    setRateState(RateState.LOADING)
    const quoteDTO = {
      // todo: If the account does not exist, return to the previous page
      userAddress: account ?? '',
      fromToken: inputToken?.address,
      toToken: outputToken?.address,
      amount: inputAmount,
      slippage,
      sendByRelayer: isSendByRelayer,
      chainId: inputToken?.chainId,
      toChainId: outputToken?.chainId,
      recipient,
    } as QuoteDTO
    const order = await sdk.swap.quote(quoteDTO)

    setLockOrder(order)
    setIsOrderExpired(false)
  }

  const buttonText = isOrderExpired
    ? t('spotswap_quote_again_btn')
    : !isSettlementFeeValid
    ? t('spotswap_swap_amount_too_low_btn')
    : t('confirm')

  const signSubmitParams = async (
    swapData: SwapData,
    protocolFee: bigint,
    gas: bigint,
    gasFeeTokenAmount: bigint,
    value: bigint,
    permit: Hex,
  ) => {
    let submitParams: SubmitDTO
    if (isSendByRelayer) {
      const sig = await sdk.swap.signTypedData(swapData, account)
      // 如果用户使用某些插件，比如 ScamSniffer，点击取消并不会抛出错误，而是返回 null。所以需要判断是否为空
      if (!sig) {
        throw new Error(SCAM_SNIFFER_CANCEL_ERR_MSG)
      }
      submitParams = {
        permit,
        minAmountOut: swapData.minMakerTokenAmount,
        strategyData: swapData.strategyData,
        uniqueId: order.uniqueId,
        slippage,
        orderSignature: sig,
        fee: {
          gasFee: gasFeeTokenAmount.toString(),
          protocolFee: protocolFee.toString(),
        },
      }
    } else {
      const l1TxOverride = {
        type: 'eip1559',
        gas,
        maxFeePerGas: gasPriceWithScale,
        maxPriorityFeePerGas,
      }
      const l2TxOverride = {
        gas,
        gasPrice: gasPriceWithScale,
      }
      const txOverride = isMainnetOrSepoliaFlag ? l1TxOverride : l2TxOverride
      const txHash = await sdk.swap.sign({
        account,
        swapData,
        value,
        takerTokenPermit: permit,
        isAcrossSwap,
        // @ts-ignore
        txOverride,
      })
      // 如果用户使用某些插件，比如 ScamSniffer，点击取消并不会抛出错误，而是返回 null。所以需要判断是否为空
      if (!txHash) {
        throw new Error(SCAM_SNIFFER_CANCEL_ERR_MSG)
      }
      submitParams = {
        permit,
        minAmountOut: swapData.minMakerTokenAmount,
        strategyData: swapData.strategyData,
        uniqueId: order.uniqueId,
        slippage,
        txHash,
        fee: {
          gasFee: gasFeeTokenAmount.toString(),
          protocolFee: protocolFee.toString(),
        },
      }
    }
    return submitParams
  }

  const checkIsGasFeeSufficient = (gasFee: bigint) => {
    if (isTakerETH && !isSendByRelayer && !isHoT) {
      const nativeTokenBalanceStr = nativeTokenBalance?.toDecimalsFixed(
        nativeTokenBalance?.decimals,
      )
      const nativeBalanceBN = toBigNumber(nativeTokenBalanceStr)
      const formattedGasFee = formatEther(gasFee)
      const cost = isTakerETH
        ? toBigNumber(inputAmount).plus(formattedGasFee)
        : toBigNumber(formattedGasFee)
      return nativeBalanceBN.gt(cost)
    }
    return true
  }

  const handleExchange = async () => {
    try {
      setIsConfirming(true)
      let permit = permitData
      const needGetTakerPermit =
        !isTakerETH &&
        !isUseAllowanceTarget &&
        (isNotEnoughTokenPermit || isPermit2AllowanceExpired)
      if (needGetTakerPermit) {
        if (!isApprovalModalOpen) togglePermitSingleModal()
        permit = await getTokenPermit({
          account,
          spender: genericSwapAddress[expectChainId],
          tokenAddress: inputToken.address as Address,
          sdk,
        })
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_SUCCESS,
        })
      }

      let gasLimit: bigint
      try {
        gasLimit = await sdk.swap.estimate({
          order,
          slippage,
          account,
          permit,
        })
        gasLimit = (gasLimit * gasLimitScale) / 100n
      } catch (e) {
        console.error(e)
        throw new Error('estimate_gas_failed')
      }
      const gasFee = gasLimit * gasPriceWithScale
      if (!checkIsGasFeeSufficient(gasFee)) {
        throw new Error('symbol_insufficient_miner_fee')
      }
      const {
        swapData,
        value,
        protocolFee,
        gasFeeTokenAmount,
      } = await sdk.swap.prepareOrder({
        order,
        slippage,
        sendByRelayer: isSendByRelayer,
        gasFee,
      })

      await delay(1000)
      let submitParams: SubmitDTO
      try {
        if (!isApprovalModalOpen) {
          toggleSubmitParamsModal()
        }

        const currentTime = Date.now()
        if (expiredTime < currentTime) throw new Error('order_expired')

        submitParams = await signSubmitParams(
          swapData,
          protocolFee,
          gasLimit,
          gasFeeTokenAmount,
          value,
          permit,
        )
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_SUCCESS,
        })
        if (isCrossSwap) {
          // @ts-ignore
          setTxHash(submitParams.txHash)
          console.time('trackTxByFrontend')
          console.time('trackTxByAPI')
        }

        try {
          await sdk.swap.submit(submitParams)
          setUniqueId(order.uniqueId)
          const status = isCrossSwap
            ? CrossOrderStatusType.OriginPending
            : OrderStatusType.Pending
          setOrderStatus(status)
        } catch (error) {
          const status = isCrossSwap
            ? CrossOrderStatusType.UnknownFailed
            : OrderStatusType.Failed
          setOrderStatus(status)
          handleTxError(error)
        }
      } catch (e) {
        const status = isCrossSwap
          ? CrossOrderStatusType.UnknownFailed
          : OrderStatusType.Failed
        setOrderStatus(status)
        const errorMsg = e.message?.toLowerCase()
        if (compareString(errorMsg, SCAM_SNIFFER_CANCEL_ERR_MSG)) {
          trackView('webtl_instant_confirm_cancel', {
            is_cross_swap: isCrossSwap,
          })
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_REJECTED,
          })
        } else if (compareString(errorMsg, POPUP_ERR_MSG)) {
          trackView('webtl_instant_confirm_cancel', {
            is_cross_swap: isCrossSwap,
          })
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_REJECTED,
          })
        } else if (errorMsg === 'order_expired') {
          message.error(t('order_expired'))
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_REJECTED,
          })
        } else if (
          Number(e.code) === 4001 ||
          errorMsg.includes('user denied') ||
          errorMsg.includes('user rejected')
        ) {
          trackView('webtl_instant_confirm_cancel', {
            is_cross_swap: isCrossSwap,
          })
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_REJECTED,
          })
        } else {
          handleTxError(e)
          updateApprovalState({
            progress: ApprovalProgress.APPROVE_REJECTED,
          })
        }
      }
    } catch (e) {
      const status = isCrossSwap
        ? CrossOrderStatusType.UnknownFailed
        : OrderStatusType.Failed
      setOrderStatus(status)
      const errorMsg = e.message?.toLowerCase()
      // getTokenPermit will handle return null in SDK if user cancels the signature
      const scamSnifferCancelErrMsg = 'cannot read properties of null'
      if (errorMsg.includes(scamSnifferCancelErrMsg)) {
        trackView('webtl_instant_confirm_cancel', {
          is_cross_swap: isCrossSwap,
        })
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_REJECTED,
        })
      } else if (compareString(errorMsg, POPUP_ERR_MSG)) {
        trackView('webtl_instant_confirm_cancel', {
          is_cross_swap: isCrossSwap,
        })
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_REJECTED,
        })
      } else if (errorMsg === 'order_expired') {
        message.error(t('order_expired'))
      } else if (errorMsg === 'estimate_gas_failed') {
        handleTxError(t('estimate_gas_failed_alert'))
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_REJECTED,
        })
      } else if (
        Number(e.code) === 4001 ||
        errorMsg.includes('user denied') ||
        errorMsg.includes('user rejected')
      ) {
        trackView('webtl_instant_confirm_cancel', {
          is_cross_swap: isCrossSwap,
        })
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_REJECTED,
        })
      } else if (errorMsg.includes('permitsingle signature is invalid')) {
        updateApprovalState({
          description1: t('permit2ContractModal.failed_description_1'),
          description2: t('permit2ContractModal.failed_description_2'),
          progress: ApprovalProgress.APPROVE_REJECTED,
        })
      } else {
        handleTxError(e)
        updateApprovalState({
          progress: ApprovalProgress.APPROVE_REJECTED,
        })
      }
    }
  }

  const handleBack = () => {
    setLockOrder(null)
    togglePreviewModal()
    if (orderState?.status === OrderStatusType.Success) {
      dispatch(
        updateSpotSwapPairAmount({
          inputAmount: '',
          outputAmount: '',
        }),
      )
      dispatch(updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.NORMAL))
    } else {
      setIsUserEdited(true)
      dispatch(updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.INQUIRING))
    }
  }

  useEffect(() => {
    isValidOrder.current = currentChainId === order?.chainId
    if (!isValidOrder.current) {
      handleBack()
    }
  }, [currentChainId, order?.chainId])

  useEffect(() => {
    const isCommonFailed =
      !isCrossSwap &&
      (orderStatus === OrderStatusType.Failed ||
        orderStatus === OrderStatusType.Timeout)
    const isCrossFailed =
      isCrossSwap && getIsFailed(orderStatus as CrossSwapOrderStatus)

    if (isOrderExpired || isCommonFailed || isCrossFailed) {
      dispatch(
        updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.ORDER_FAILED),
      )
    }
  }, [isOrderExpired, orderStatus])

  useEffect(() => {
    const getPreview = async () => {
      try {
        const gasUsed = BigInt(order.gasUsed)
        const gasUsedWithScale = applyGasUsedBuffer(gasUsed)
        let gasLimit = gasUsedWithScale || defaultGasLimit
        if (!gasUsedWithScale && !!account) {
          try {
            gasLimit = await sdk.swap.estimate({
              order,
              slippage,
              account,
              permit: permitData,
            })
            gasLimit = (gasLimit * gasLimitScale) / 100n
          } catch (error) {}
        }
        if (!isValidOrder.current) return
        const gasFee = gasLimit * gasPriceWithScale
        const {
          swapData,
          fee,
          gasFeeTokenAmount,
        } = await sdk.swap.prepareOrder({
          order,
          gasFee,
          slippage,
          sendByRelayer: isSendByRelayer,
        })

        if (!isValidOrder.current) return

        const makerTokenAmount = isCrossSwap
          ? parseUnits(order.amountOut, outputToken?.decimals)
          : swapData.makerTokenAmount
        const minMakerTokenAmount = isCrossSwap
          ? parseUnits(order.amountOut, outputToken?.decimals)
          : swapData.minMakerTokenAmount

        const formattedOutputAmount = formatUnits(
          BigInt(makerTokenAmount),
          outputToken.decimals,
        )
        dispatch(
          updateSpotSwapAmount({
            direction: TradeDirection.Output,
            amount: formattedOutputAmount,
          }),
        )
        setOutputTokenAmount(formattedOutputAmount)
        setAmountOut(makerTokenAmount)
        setGasFeeTokenAmount(gasFeeTokenAmount)
        setMinAmountOut(minMakerTokenAmount)
        setFee(fee)
        setFeeFactor(order.feeFactor)
        if (isAcrossSwap) {
          // @ts-ignore
          const estimatedSec = order.routes[0][0][0].route?.estimatedFillTimeSec
          setCrossEstimatedSec(estimatedSec)
        }
        setRateState(RateState.OK)
        // after refreshing order, the status of the mascot needs to be changed
        dispatch(
          updateSpotSwapUserTradeStatus(
            SpotSwapUserTradeStatus.PREVIEWING_ORDER,
          ),
        )
      } catch (error) {
        console.error(error)
      }
    }

    if (order && !isConfirming && !isOrderExpired && gasPriceWithScale > 0n) {
      setOrderRefreshing(true)
      getPreview().finally(() => {
        setOrderRefreshing(false)
      })
    }
  }, [
    order?.uniqueId,
    isConfirming,
    isOrderExpired,
    permitData,
    gasLimitScale,
    gasPriceWithScale,
  ])

  useEffect(() => {
    if (uniqueId) {
      if (!isCrossSwap || !isTrackingCrossTxByEvent) {
        setOrderStatus(orderState?.status)
      }

      if (
        isCrossSwap &&
        orderState?.status === CrossOrderStatusType.DestinationSuccess
      ) {
        console.timeEnd('trackTxByAPI')
      }
    }
  }, [uniqueId, orderState?.status, isTrackingCrossTxByEvent])

  // 刷新报价
  useEffect(() => {
    if (isOrderExpired) {
      const initialExpiredTime =
        isOrderContainsBebop && !!order?.swapData?.expiry
          ? (order?.swapData?.expiry - userSignatureTime) * 1000
          : Date.now() + mmOrderExpirationSeconds * 1000

      setExpiredTime(initialExpiredTime)
      setIsOrderExpired(false)
      setOrderRefreshing(false)
    }
  }, [order.uniqueId, isOrderContainsBebop])

  useEffect(() => {
    const isCommonSuccess =
      !isCrossSwap && orderState?.status === OrderStatusType.Success
    const isCrossSuccess =
      isCrossSwap && getIsSuccess(orderState?.status as CrossSwapOrderStatus)

    if (uniqueId && (isCommonSuccess || isCrossSuccess)) {
      const { gasFeeAmount, feeAmount, settleAmount } = orderState
      if (gasFeeAmount && feeAmount && settleAmount) {
        setGasFeeTokenAmount(BigInt(gasFeeAmount))
        setAmountOut(BigInt(settleAmount))
        setOutputTokenAmount(
          formatUnits(BigInt(settleAmount), outputToken.decimals),
        )
        setFee(BigInt(feeAmount))
      }
      dispatch(
        updateSpotSwapUserTradeStatus(SpotSwapUserTradeStatus.ORDER_COMPLETED),
      )
    }
  }, [uniqueId, orderState])

  useEffect(() => {
    //  status 为 success 时，amountIn 和 settleAmount 不一定会立刻有值
    const isCommonSuccess =
      !isCrossSwap && orderStatus === OrderStatusType.Success
    const isCrossSuccess =
      isCrossSwap && getIsSuccess(orderStatus as CrossSwapOrderStatus)
    if (
      (isCommonSuccess || isCrossSuccess) &&
      !!orderState?.amountIn &&
      !!orderState?.settleAmount
    ) {
      setTimeout(() => {
        setShowSuccessful(true)
      }, 400)
    }
  }, [orderStatus, orderState])

  useEffect(() => {
    if (txHash) {
      trackCrossChainTx({
        fromTxHash: txHash,
        fromChainId: inputToken.chainId,
        toChainId: outputToken.chainId,
        recipient,
        setOrderStatus,
        setIsTrackingCrossTxByEvent,
      })
    }
  }, [txHash])

  if (!isPreviewOpen) return null

  if (showSuccessful) {
    return (
      <OrderSuccessful
        orderState={orderState}
        isPMM={!isAMMOrder}
        handleBack={handleBack}
      />
    )
  }

  return (
    <StyledOrderPreview>
      <InteractiveMascot />
      <BackButton disabled={isConfirming && isUnbroadcast} onClick={handleBack}>
        <img src={IconSwapArrow} />
      </BackButton>
      <Title>
        <Countdown
          autoStart
          date={expiredTime}
          onComplete={() => {
            setIsOrderExpired(!isConfirming ? true : false)
          }}
          renderer={({ minutes, seconds }) => (
            <span>
              {isConfirming ? (
                t('review_btn')
              ) : minutes * 60 + seconds > 0 ? (
                <CountdownTitle>
                  {
                    <Trans
                      i18nKey="spotswap_quote_validity"
                      values={{ countdown: minutes * 60 + seconds }}
                      components={{
                        special: <CountdownNumber />,
                      }}
                    />
                  }
                </CountdownTitle>
              ) : (
                <QuoteExpiredTitle>
                  {t('spotswap_quote_expired')}
                </QuoteExpiredTitle>
              )}
            </span>
          )}
          key={expiredTime}
        />
      </Title>
      <Content>
        <div>
          <TokensChangeArea
            inputTokenAmount={inputTokenAmount}
            outputTokenAmount={outputTokenAmount}
            loading={componentLoading}
          />
          <StyledDivider />
          <TradeForm.Stack gap={8}>
            <TradeFee
              fee={fee}
              feeFactor={feeFactor}
              isCrossSwap={isCrossSwap}
              loading={componentLoading}
            />

            <ReceivedRow
              amountOut={amountOut}
              fee={fee}
              loading={componentLoading}
            />
            <StyledDividerLite />
            {isAMMOrder && !isCrossSwap && (
              <>
                <SlippageRow slippage={slippage} loading={componentLoading} />
                <MinReceived amount={minAmountOut} loading={componentLoading} />
                <StyledDividerLite />
              </>
            )}
            <Row>
              <span>{t('rate')}</span>
              <StyledTipValue className="rate-detail">
                <OrderRefreshing
                  loading={componentLoading}
                  renderText={`1 ${inputToken?.symbol} = ${formatAmount(
                    rate,
                    Math.min(calcScale, outputToken?.decimals ?? calcScale),
                  )} ${outputToken?.symbol} `}
                />
              </StyledTipValue>
            </Row>
            <GasFeeRow
              loading={componentLoading}
              gasFeeTokenAmount={gasFeeTokenAmount}
            />
            {isCrossSwap && (
              <>
                <CrossEstimatedTime
                  loading={componentLoading}
                  estimatedSec={crossEstimatedSec}
                />
                {isContractWallet && <RecipientPreview />}
              </>
            )}
            {!isSendByRelayer && !orderRefreshing && !isOrderExpired && (
              <GasFeeRiskCalculator gasFeeTokenAmount={gasFeeTokenAmount} />
            )}
            {isSendByRelayer && !orderRefreshing && !isOrderExpired && (
              <SettlementFeeRiskCalculator fee={fee} />
            )}
          </TradeForm.Stack>
          {/* <StyledRewards>
            <span>{t('trading_rewards')}</span>
            <StyledRewardsValueArea
              data-trackid="webtl_instant_preview_award"
              data-trackdata={JSON.stringify({ is_cross_swap: isCrossSwap })}
              onClick={(e) => trackElClickCurring(e)(toggleLonRewardModal)}
            >
              <img src={IconLonToken} />
              <span>{t('check_lon_rewards')}</span>
              <img src={IconArrow} />
            </StyledRewardsValueArea>
          </StyledRewards> */}
        </div>

        {!isConfirming && (
          <>
            {/* price impact过大时的确认框 */}
            {priceImpactIsDanger && !isOrderExpired && (
              <PriceImpactConfirm
                checked={highPriceImpactConfirm}
                setChecked={(val) => {
                  setHighPriceImpactConfirm(val)
                }}
              />
            )}

            <ExchangeButton
              loading={componentLoading}
              handleExchange={handleClick}
              text={buttonText}
              // 当价格影响达到DANGER，而且用户未确认价格影响，而且按钮状态是确认时，需要禁用
              disabled={
                ((priceImpactIsDanger && !highPriceImpactConfirm) ||
                  !isSettlementFeeValid) &&
                !isOrderExpired
              }
            />
          </>
        )}
        {isConfirming && (
          <OrderStatus
            status={(orderStatus as unknown) as TRADE_STATUS}
            isCrossSwap={isCrossSwap}
            isUnbroadcast={isUnbroadcast}
          />
        )}
      </Content>
      <ConfirmRecipientModal
        isOpen={isOpenConfirmRecipientModal}
        onClose={recipientModalCloseHandler.current}
      />
    </StyledOrderPreview>
  )
}

export default OrderPreview
